import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Pause, Play } from 'lucide-react';
import { trackMiniAppEvent } from '../../utils/analytics';
import {
  engineShell,
  engineShellGuided,
  titleStyle,
  instructionsStyle,
  primaryBtn,
  secondaryBtn,
  feedbackCorrect,
  feedbackIncorrect,
} from './shared/engineTheme';
import { useCelebration, CelebrationOverlay, A11yAnnouncement } from './shared/Celebration';
import { useGuidedHints } from './shared/useGuidedHints';
import GuidedHintOverlay, {
  GuidedHintHelpButton,
  guidedHintReadAloud,
} from './shared/GuidedHintOverlay';
import { SIMULATION_SCRUB_ONE_SHOT } from './shared/engineInteractionOneShots';
import { isTapFirstLessonUi } from './shared/lessonUiMode';

/**
 * Frame-based simulation with a scrubber. Sprites move via linear interpolation between keyframes.
 *
 * Content shape:
 * {
 *   instructions?: string,
 *   scene: {
 *     viewBox?: '0 0 200 120',
 *     background?: [{ type: 'rect'|'circle'|'path', ... }],
 *     decorations?: [{ type: 'text'|'emoji', x, y, content, fontSize? }],
 *   },
 *   totalFrames: number,
 *   sprites: [
 *     {
 *       id, type: 'emoji'|'circle'|'rect',
 *       content?: string,           // for 'emoji' (or text)
 *       fontSize?: number,
 *       fill?, stroke?, strokeWidth?, r?, width?, height?,
 *       keyframes: [{ frame, x, y }],
 *     },
 *   ],
 *   task: {
 *     type?: 'stop_at_frame',       // currently the only supported type
 *     correctFrame: number,
 *     tolerance?: number,           // default 3
 *     promptHint?: string,
 *   },
 *   victory?: { message?: string },
 * }
 *
 * onComplete payload:
 *   { activityId, engine: 'simulation_scrub', perfect: boolean, attempts: number, distance: number }
 *
 * Guided shell (`onRegisterLessonFooterActions`): **Check** in the lesson footer; in-engine **Mark this moment** is hidden.
 */

const DEFAULT_VIEWBOX = '0 0 200 120';

function parseViewBox(vb) {
  const parts = String(vb || DEFAULT_VIEWBOX)
    .trim()
    .split(/\s+/)
    .map(Number);
  if (parts.length !== 4 || parts.some((n) => !Number.isFinite(n))) return [0, 0, 200, 120];
  return parts;
}

function interpolateSprite(sprite, frame) {
  const kf = (sprite.keyframes || []).slice().sort((a, b) => a.frame - b.frame);
  if (kf.length === 0) return { x: 0, y: 0 };
  if (kf.length === 1) return { x: kf[0].x ?? 0, y: kf[0].y ?? 0 };
  if (frame <= kf[0].frame) return { x: kf[0].x ?? 0, y: kf[0].y ?? 0 };
  const last = kf[kf.length - 1];
  if (frame >= last.frame) return { x: last.x ?? 0, y: last.y ?? 0 };
  for (let i = 0; i < kf.length - 1; i += 1) {
    const a = kf[i];
    const b = kf[i + 1];
    if (frame >= a.frame && frame <= b.frame) {
      const span = b.frame - a.frame || 1;
      const t = (frame - a.frame) / span;
      return {
        x: (a.x ?? 0) + ((b.x ?? 0) - (a.x ?? 0)) * t,
        y: (a.y ?? 0) + ((b.y ?? 0) - (a.y ?? 0)) * t,
      };
    }
  }
  return { x: last.x ?? 0, y: last.y ?? 0 };
}

function SceneBackground({ scene, vbW, vbH }) {
  const background = scene?.background || [];
  const decorations = scene?.decorations || [];
  return (
    <>
      {background.map((b, i) => {
        if (b.type === 'rect') {
          return (
            <rect
              key={`bg-${i}`}
              x={b.x ?? 0}
              y={b.y ?? 0}
              width={b.width ?? vbW}
              height={b.height ?? vbH}
              fill={b.fill || '#e0f2fe'}
              opacity={b.opacity ?? 1}
            />
          );
        }
        if (b.type === 'circle') {
          return (
            <circle
              key={`bg-${i}`}
              cx={b.cx ?? 0}
              cy={b.cy ?? 0}
              r={b.r ?? 1}
              fill={b.fill || '#fde68a'}
              opacity={b.opacity ?? 1}
            />
          );
        }
        if (b.type === 'path' && typeof b.d === 'string') {
          return (
            <path
              key={`bg-${i}`}
              d={b.d}
              fill={b.fill || 'none'}
              stroke={b.stroke || 'none'}
              strokeWidth={b.strokeWidth ?? 1}
              opacity={b.opacity ?? 1}
            />
          );
        }
        return null;
      })}
      {decorations.map((d, i) =>
        d.type === 'text' || d.type === 'emoji' ? (
          <text
            key={`dec-${i}`}
            x={d.x ?? 0}
            y={d.y ?? 0}
            fontSize={d.fontSize ?? 6}
            textAnchor={d.textAnchor || 'middle'}
            fill={d.fill || 'currentColor'}
            opacity={d.opacity ?? 0.9}
          >
            {d.content}
          </text>
        ) : null,
      )}
    </>
  );
}

function SpriteAtFrame({ sprite, frame }) {
  const { x, y } = interpolateSprite(sprite, frame);
  if (sprite.type === 'circle') {
    return (
      <circle
        cx={x}
        cy={y}
        r={sprite.r ?? 4}
        fill={sprite.fill || '#0ea5e9'}
        stroke={sprite.stroke || 'none'}
        strokeWidth={sprite.strokeWidth ?? 0}
      />
    );
  }
  if (sprite.type === 'rect') {
    const w = sprite.width ?? 8;
    const h = sprite.height ?? 8;
    return (
      <rect
        x={x - w / 2}
        y={y - h / 2}
        width={w}
        height={h}
        fill={sprite.fill || '#0ea5e9'}
        stroke={sprite.stroke || 'none'}
        strokeWidth={sprite.strokeWidth ?? 0}
        rx={sprite.rx ?? 1}
        ry={sprite.ry ?? 1}
      />
    );
  }
  // emoji / text
  return (
    <text
      x={x}
      y={y}
      fontSize={sprite.fontSize ?? 12}
      textAnchor="middle"
      dominantBaseline="middle"
    >
      {sprite.content ?? ''}
    </text>
  );
}

export default function SimulationScrubEngine({
  title = 'Run the simulation',
  content = {},
  hints = [],
  explanation,
  activityId = 'engine_simulation_scrub',
  onProgress,
  onComplete,
  guidedHintsEnabled = true,
  lessonUiMode = 'default',
  onRegisterLessonFooterActions,
}) {
  const shellLesson = typeof onRegisterLessonFooterActions === 'function';
  const scene = content.scene;
  const sprites = useMemo(() => content.sprites || [], [content.sprites]);
  const total = Math.max(1, Number(content.totalFrames) || 60);
  const tapFirst = isTapFirstLessonUi(lessonUiMode);
  const frameJump = Math.max(1, Math.round(total / 20));
  const task = content.task || {};
  const tolerance = Number(task.tolerance) || 3;
  const correctFrame = Math.max(0, Math.min(total, Number(task.correctFrame) || 0));
  const promptHint = task.promptHint || 'Stop the playback at the right moment.';
  const victoryMsg = content.victory?.message || 'Right on time!';

  const [vbX, vbY, vbW, vbH] = parseViewBox(scene?.viewBox);
  const aspect = vbW > 0 && vbH > 0 ? `${vbW} / ${vbH}` : '5 / 3';

  const [frame, setFrame] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [feedback, setFeedback] = useState(null);
  const [a11yMsg, setA11yMsg] = useState('');
  const [activityComplete, setActivityComplete] = useState(false);
  const [showCelebration, celebrate] = useCelebration();
  const completedRef = useRef(false);
  const rafRef = useRef(null);
  const lastTickRef = useRef(0);

  const getExplanation = useCallback(() => content.explanation, [content.explanation]);

  const guided = useGuidedHints({
    activityId,
    hints,
    oneShotInteractionHint: SIMULATION_SCRUB_ONE_SHOT,
    explanation,
    getExplanation,
    isActivityComplete: activityComplete,
    enabled: guidedHintsEnabled,
  });

  const { noteUserAction, noteIncorrectAnswer, noteCorrectAnswer, resetSession } = guided;

  // Auto-advance frames at ~30fps when playing
  useEffect(() => {
    if (!playing) return undefined;
    const fps = 30;
    const step = 1000 / fps;
    const tick = (t) => {
      if (!lastTickRef.current) lastTickRef.current = t;
      const elapsed = t - lastTickRef.current;
      if (elapsed >= step) {
        const advance = Math.floor(elapsed / step);
        setFrame((f) => {
          const next = f + advance;
          if (next >= total) {
            setPlaying(false);
            return total;
          }
          return next;
        });
        lastTickRef.current = t;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      lastTickRef.current = 0;
    };
  }, [playing, total]);

  const togglePlay = () => {
    noteUserAction();
    if (frame >= total) setFrame(0);
    setPlaying((p) => !p);
  };

  const onScrub = (e) => {
    noteUserAction();
    const v = Number(e.target.value);
    if (Number.isFinite(v)) {
      setPlaying(false);
      setFrame(Math.max(0, Math.min(total, v)));
    }
  };

  const bumpFrame = (delta) => {
    noteUserAction();
    setPlaying(false);
    setFrame((f) => Math.max(0, Math.min(total, f + delta)));
  };

  const handleMark = useCallback(() => {
    noteUserAction();
    setPlaying(false);
    setAttempts((a) => a + 1);
    const distance = Math.abs(frame - correctFrame);
    if (distance <= tolerance) {
      setFeedback({ type: 'correct', message: victoryMsg });
      setA11yMsg(victoryMsg);
      onProgress?.({
        activityId,
        engine: 'simulation_scrub',
        phase: 'mark',
        frame,
        correct: true,
        distance,
      });
      trackMiniAppEvent.interaction(activityId, 'mark_correct');
      if (!completedRef.current) {
        completedRef.current = true;
        setActivityComplete(true);
        noteCorrectAnswer();
        celebrate();
        onComplete?.({
          activityId,
          engine: 'simulation_scrub',
          perfect: attempts === 0,
          attempts: attempts + 1,
          distance,
        });
        trackMiniAppEvent.completed(activityId);
      }
    } else {
      setFeedback({
        type: 'wrong',
        message: distance > tolerance + 5
          ? `Too far. Try scrubbing closer to the moment described.`
          : `Almost — slide a bit ${frame < correctFrame ? 'forward' : 'back'} and try again.`,
      });
      setA11yMsg('Not quite. Try again.');
      onProgress?.({
        activityId,
        engine: 'simulation_scrub',
        phase: 'mark',
        frame,
        correct: false,
        distance,
      });
      trackMiniAppEvent.interaction(activityId, 'mark_wrong');
      noteIncorrectAnswer();
    }
  }, [
    activityId,
    attempts,
    celebrate,
    correctFrame,
    frame,
    noteCorrectAnswer,
    noteIncorrectAnswer,
    noteUserAction,
    onComplete,
    onProgress,
    tolerance,
    victoryMsg,
  ]);

  const revealAnswer = useCallback(() => {
    if (activityComplete) return;
    noteUserAction();
    setPlaying(false);
    setFrame(correctFrame);
    setFeedback(null);
  }, [activityComplete, correctFrame, noteUserAction]);

  useEffect(() => {
    if (!onRegisterLessonFooterActions) return undefined;
    if (!shellLesson || activityComplete) {
      onRegisterLessonFooterActions(null);
      return undefined;
    }
    const showAnswer = Array.isArray(hints) && hints.length > 0 ? revealAnswer : undefined;
    onRegisterLessonFooterActions({
      checkAnswer: handleMark,
      checkAnswerDisabled: false,
      ...(showAnswer ? { showAnswer } : {}),
    });
    return () => onRegisterLessonFooterActions(null);
  }, [
    activityComplete,
    handleMark,
    hints,
    onRegisterLessonFooterActions,
    revealAnswer,
    shellLesson,
  ]);

  const reset = () => {
    completedRef.current = false;
    setActivityComplete(false);
    resetSession();
    setFrame(0);
    setPlaying(false);
    setAttempts(0);
    setFeedback(null);
  };

  if (!scene || sprites.length === 0) {
    return (
      <div style={engineShell}>
        <p style={instructionsStyle}>This simulation has no scene or sprites yet.</p>
      </div>
    );
  }

  return (
    <div style={{ ...(shellLesson ? { ...engineShellGuided, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' } : { ...engineShell, maxWidth: 560 }), position: 'relative', ...(guided.contentReserveStyle || {}) }}>
      <CelebrationOverlay visible={showCelebration} message={victoryMsg} />
      <A11yAnnouncement message={a11yMsg} />
      <GuidedHintOverlay
        open={guided.open}
        isNarrow={guided.isNarrow}
        visibleLevel={guided.visibleLevel}
        maxLevel={guided.maxLevel}
        isInteractionPhase={guided.isInteractionPhase}
        anotherClueEnabled={guided.anotherClueEnabled}
        hintEntry={guided.hintEntry}
        showHowAvailable={guided.showHowAvailable}
        resolvedExplanation={guided.resolvedExplanation}
        explanationOpen={guided.explanationOpen}
        onDismiss={guided.dismissOverlay}
        onAnotherClue={guided.anotherClue}
        onReadAloud={() =>
          guidedHintReadAloud(guided.hintEntry, activityId, guided.readAloudTrackLevel)
        }
        onShowHow={guided.openExplanation}
      />
      <GuidedHintHelpButton visible={guided.showHelpButton} onClick={guided.requestHintManually} />

      {!tapFirst ? <h2 style={titleStyle}>{title}</h2> : null}
      {!tapFirst && content.instructions ? <p style={instructionsStyle}>{content.instructions}</p> : null}
      {!tapFirst && promptHint ? (
        <p style={{ ...instructionsStyle, fontWeight: 700, marginBottom: 12 }}>{promptHint}</p>
      ) : null}

      <div
        style={{
          position: 'relative',
          width: '100%',
          ...(tapFirst && shellLesson ? { flex: 1, minHeight: 140 } : { aspectRatio: aspect }),
          borderRadius: 16,
          overflow: 'hidden',
          background: scene.background?.length ? 'transparent' : '#e0f2fe',
          border: '1px solid var(--color-border)',
        }}
      >
        <svg
          viewBox={`${vbX} ${vbY} ${vbW} ${vbH}`}
          preserveAspectRatio="none"
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
        >
          <SceneBackground scene={scene} vbW={vbW} vbH={vbH} />
          {sprites.map((s) => (
            <SpriteAtFrame key={s.id} sprite={s} frame={frame} />
          ))}
        </svg>
      </div>

      {/* Row 1: Play/Pause + Previous/Next (tapFirst) or scrubber + counter (desktop) */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 14, flexShrink: 0 }}>
        <button
          type="button"
          onClick={togglePlay}
          aria-label={playing ? 'Pause' : 'Play'}
          style={{
            ...secondaryBtn,
            width: 'auto',
            flex: '0 0 auto',
            minHeight: tapFirst ? 52 : 44,
            padding: tapFirst ? '10px 16px' : '8px 14px',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          {playing ? <Pause size={18} aria-hidden /> : <Play size={18} aria-hidden />}
          {playing ? 'Pause' : 'Play'}
        </button>
        {tapFirst ? (
          <>
            <button
              type="button"
              onClick={() => bumpFrame(-1)}
              aria-label="Previous frame"
              style={{
                ...secondaryBtn,
                flex: 1,
                minHeight: 52,
                padding: '10px 8px',
                fontWeight: 800,
              }}
            >
              ◀ Prev
            </button>
            <button
              type="button"
              onClick={() => bumpFrame(1)}
              aria-label="Next frame"
              style={{
                ...secondaryBtn,
                flex: 1,
                minHeight: 52,
                padding: '10px 8px',
                fontWeight: 800,
              }}
            >
              Next ▶
            </button>
          </>
        ) : (
          <>
            <input
              type="range"
              min={0}
              max={total}
              step={1}
              value={frame}
              onChange={onScrub}
              aria-label={`Scrub to frame ${frame} of ${total}`}
              style={{ flex: 1, minWidth: 120 }}
            />
            <span
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: 'var(--color-text)',
                opacity: 0.7,
                minWidth: 52,
                textAlign: 'right',
                flexShrink: 0,
              }}
            >
              {frame} / {total}
            </span>
          </>
        )}
      </div>

      {/* Row 2 (tapFirst only): −N | frame counter | +N */}
      {tapFirst ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, flexShrink: 0 }}>
          <button
            type="button"
            onClick={() => bumpFrame(-frameJump)}
            aria-label={`Back ${frameJump} frames`}
            style={{
              ...secondaryBtn,
              flex: 1,
              minHeight: 48,
              padding: '8px 6px',
              fontWeight: 800,
            }}
          >
            −{frameJump}
          </button>
          <span
            style={{
              flex: 2,
              textAlign: 'center',
              fontSize: 11,
              fontWeight: 700,
              color: 'var(--color-text)',
              opacity: 0.7,
            }}
          >
            {frame} / {total}
          </span>
          <button
            type="button"
            onClick={() => bumpFrame(frameJump)}
            aria-label={`Forward ${frameJump} frames`}
            style={{
              ...secondaryBtn,
              flex: 1,
              minHeight: 48,
              padding: '8px 6px',
              fontWeight: 800,
            }}
          >
            +{frameJump}
          </button>
        </div>
      ) : null}

      {!shellLesson ? (
        <button
          type="button"
          style={{ ...primaryBtn, marginTop: 14, minHeight: tapFirst ? 56 : primaryBtn.minHeight }}
          onClick={handleMark}
        >
          Mark this moment
        </button>
      ) : null}

      {feedback ? (
        <p
          style={{
            ...(feedback.type === 'correct' ? feedbackCorrect : feedbackIncorrect),
            marginTop: 14,
          }}
          role="status"
        >
          {feedback.message}
        </p>
      ) : null}

      <div style={{ display: 'flex', gap: 10, marginTop: 14, flexWrap: 'wrap' }}>
        {attempts > 0 ? (
          <button
            type="button"
            style={{ ...secondaryBtn, width: 'auto', flex: '0 0 auto', minHeight: 40, padding: '8px 14px' }}
            onClick={reset}
          >
            Reset
          </button>
        ) : null}
      </div>
    </div>
  );
}
